import React, { useState, useEffect, useImperativeHandle } from 'react';
import { useForm } from 'react-hook-form';
import {
  FormControl,
  FormLabel,
  FormErrorMessage,
  Input,
  Select,
  VStack,
  Grid,
  GridItem,
  Textarea,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Box,
  Text,
  Button,
  HStack,
  SimpleGrid,
  useColorModeValue,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
} from '@chakra-ui/react';
import { useQuery } from 'react-query';
import { getClients } from '../../services/clientService';
import { getConsultants, getAvailableTimeSlots } from '../../services/consultantService';
import { useAuth } from '../../contexts/AuthContext';
import JalaliDatePicker from '../JalaliDatePicker';
import SearchableSelect from '../SearchableSelect';
import { FaArrowRight, FaArrowLeft, FaCalendarAlt, FaClock, FaUser, FaUserTie, FaExclamationTriangle } from 'react-icons/fa';
import moment from 'moment-jalaali';
import { jalaliToGregorian as jalaliToGregorianUtil } from '../../utils/timeUtils';

/**
 * فرم چند مرحله‌ای برای ثبت ملاقات
 */
const MultiStepAppointmentForm = React.forwardRef(({ appointment, onSubmit, isLoading }, ref) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedClient, setSelectedClient] = useState('');
  const [clientConsultant, setClientConsultant] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  
  const { isAdmin } = useAuth();
  const textColor = useColorModeValue("gray.700", "white");
  const bgCard = useColorModeValue("white", "gray.700");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const primaryColor = useColorModeValue("teal.500", "teal.300");

  // دریافت لیست مراجعان
  const { data: clientsData } = useQuery(['clients'], () => getClients({ limit: 1000 }), {
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false
  });
  const clients = clientsData?.data?.clients || clientsData?.clients || clientsData?.data || clientsData || [];

  // دریافت لیست مشاوران
  const { data: consultantsData } = useQuery(['consultants'], () => getConsultants({ limit: 100 }), {
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false
  });
  const consultants = consultantsData?.data?.consultants || consultantsData?.consultants || consultantsData?.data || consultantsData || [];

  // دریافت ساعات خالی مشاور
 
  
  const { data: availableSlotsData, isLoading: slotsLoading, error: slotsError } = useQuery(
    ['available-time-slots', selectedDate, clientConsultant?._id],
    () => {
      // استفاده از مشاور مراجع انتخاب شده یا مشاور پیش‌فرض
      const consultantId = clientConsultant?._id || '68b3425e14fa0a2d792f8054'; // مشاور پیش‌فرض: تست تست تر
      return getAvailableTimeSlots(consultantId, selectedDate);
    },
    {
      enabled: !!selectedDate,
      staleTime: 2 * 60 * 1000,
      cacheTime: 5 * 60 * 1000,
      
      onError: (slotsError) => {
        console.error('❌ API call ناموفق:', slotsError);
      }
    }
  );
  

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    trigger,
    getValues,
  } = useForm({
    defaultValues: {
      clientId: appointment?.clientId || appointment?.client?._id || '',
      consultantId: appointment?.consultantId || appointment?.consultant?._id || '',
      date: appointment?.date || '',
      time: appointment?.time || '',
      duration: appointment?.duration || 60,
      price: appointment?.cost || appointment?.price || '',
      consultantNotes: appointment?.consultantNotes || '',
      companionName: appointment?.companionName || '',
      companionRelation: appointment?.companionRelation || '',
      ...(isAdmin() && {
        status: appointment?.status || 'scheduled',
        paymentStatus: appointment?.paymentStatus || 'pending',
        meetingType: appointment?.meetingType || 'in-person',
        notes: appointment?.notes || '',
      }),
    },
    mode: 'onChange',
  });

  // تبدیل تاریخ میلادی به شمسی برای نمایش
  useEffect(() => {
    if (appointment?.date && !appointment.date.includes('/')) {
      try {
        const gregorianMoment = moment(appointment.date);
        if (gregorianMoment.isValid()) {
          const jalaliString = gregorianMoment.format('jYYYY/jMM/jDD');
          setSelectedDate(jalaliString);
          setValue('date', jalaliString);
        }
      } catch (error) {
        console.error('Error converting date to Jalali:', error);
      }
    }
  }, [appointment?.date, setValue]);

  // بررسی مشاور مراجع انتخاب شده و تنظیم طول جلسه مطابق مشاور
  useEffect(() => {
    if (selectedClient) {
      const client = clients.find(c => c._id === selectedClient);
      if (client && client.consultant) {
        setClientConsultant(client.consultant);
        setValue('consultantId', client.consultant._id);
        setValue('duration', client.consultant.sessionDuration || 60);
      } else {
        setClientConsultant(null);
        setValue('consultantId', '');
      }
    }
  }, [selectedClient, clients, setValue]);

  // تولید ساعات پیشنهادی
  useEffect(() => {
  
    
    if (selectedDate && availableSlotsData) {
      generateTimeSlots();
    } else if (selectedDate) {
    } else {
    }
  }, [selectedDate, availableSlotsData]); // اضافه کردن availableSlotsData

  const generateTimeSlots = () => {


    if (!availableSlotsData?.data?.slots) {
      setAvailableSlots([]);
      return;
    }


    // تنظیم طول جلسه بر اساس مشاور (اگر موجود باشد)
    if (clientConsultant?.sessionDuration) {
      setValue('duration', clientConsultant.sessionDuration);
    }

    console.log('availableSlotsData.data.slots',availableSlotsData.data.slots)
    // تبدیل ساعات دریافتی از backend به فرمت مورد نیاز
    

    setAvailableSlots(availableSlotsData.data.slots);
  };

  // Note: Using utility functions from timeUtils.js

  const handleDateChange = (date) => {

    
    setSelectedDate(date);
    setValue('date', date);
    setValue('time', ''); // پاک کردن زمان انتخاب شده
    setAvailableSlots([]); // پاک کردن ساعات پیشنهادی
    trigger(['date']);
  };

  const handleClientChange = (clientId) => {
    setSelectedClient(clientId);
    setValue('clientId', clientId);
    setValue('time', ''); // پاک کردن زمان انتخاب شده
    setAvailableSlots([]); // پاک کردن ساعات پیشنهادی
    trigger(['clientId']);
  };

  const handleTimeSelect = (time) => {
    setValue('time', time);
    trigger(['time']);
  };

  const nextStep = async () => {
    let isStepValid = false;
    
    if (currentStep === 1) {
      // بررسی انتخاب مراجع و وجود مشاور
      const clientValid = await trigger(['clientId']);
      if (clientValid && !clientConsultant) {
        // اگر مراجع مشاور ندارد، اجازه ادامه نده
        return;
      }
      isStepValid = clientValid && !!clientConsultant;
    } else if (currentStep === 2) {
      // بررسی فیلدهای مرحله 2
      const dateValid = selectedDate && selectedDate.trim() !== '';
      const timeValid = watch('time') && watch('time').trim() !== '';
      
      // تنظیم مقادیر برای validation
      if (dateValid) {
        setValue('date', selectedDate);
      }
      if (timeValid) {
        setValue('time', watch('time'));
      }
      
      // trigger validation
      const dateValidation = await trigger('date');
      const timeValidation = await trigger('time');
      
      isStepValid = dateValid && timeValid && dateValidation && timeValidation;
    } else {
      isStepValid = true;
    }
    
    if (isStepValid) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    setCurrentStep(currentStep - 1);
  };

  // اضافه کردن validation برای فیلدهای تاریخ و زمان
  useEffect(() => {
    if (selectedDate) {
      setValue('date', selectedDate);
    }
  }, [selectedDate, setValue]);

  useEffect(() => {
    const timeValue = watch('time');
    if (timeValue) {
      setValue('time', timeValue);
    }
  }, [watch, setValue]);

  const getStepTitle = (step) => {
    switch (step) {
      case 1:
        return 'انتخاب مراجع';
      case 2:
        return 'انتخاب تاریخ و زمان';
      case 3:
        return 'تنظیمات تکمیلی';
      case 4:
        return 'مرور و ثبت';
      default:
        return '';
    }
  };

  const getStepIcon = (step) => {
    switch (step) {
      case 1:
        return <FaUser />;
      case 2:
        return <FaCalendarAlt />;
      case 3:
        return <FaClock />;
      case 4:
        return <FaUserTie />;
      default:
        return null;
    }
  };

  const handleFormSubmit = (data) => {
    // تبدیل تاریخ شمسی به میلادی برای ارسال به سرور
    if (data.date && data.date.includes('/')) {
     
      
      const gregorianDate = jalaliToGregorianUtil(data.date);
      
      if (gregorianDate) {
        const isoDate = gregorianDate.toISOString().split('T')[0];
        data.date = isoDate;
      }
    }
    onSubmit(data);
  };

  // قرار دادن getValues در ref
  useImperativeHandle(ref, () => ({
    getValues: () => getValues()
  }));

  const renderStep1 = () => (
    <VStack spacing={6} align="stretch">
      <Box p={4} bg="blue.50" borderRadius="lg" border="1px" borderColor="blue.200">
        <Text fontSize="sm" color="blue.600" fontWeight="semibold">
          مرحله 1: انتخاب مراجع
        </Text>
        <Text fontSize="xs" color="blue.500" mt={1}>
          ابتدا مراجع مورد نظر را انتخاب کنید
        </Text>
      </Box>

      <FormControl isInvalid={!!errors.clientId}>
        <FormLabel>مراجع *</FormLabel>
        <SearchableSelect
          options={clients}
          value={watch('clientId')}
          onChange={(value) => {
            setValue('clientId', value);
            handleClientChange(value);
            trigger('clientId');
          }}
          placeholder="مراجع را انتخاب کنید"
          searchPlaceholder="جستجو در مراجعین..."
          getOptionLabel={(client) => `${client.firstName} ${client.lastName} - ${client.nationalId || 'بدون کد ملی'}`}
          getOptionValue={(client) => client._id}
          required={true}
          isInvalid={!!errors.clientId}
          errorMessage={errors.clientId?.message}
        />
      </FormControl>

      {/* نمایش اطلاعات مشاور مراجع */}
      {selectedClient && clientConsultant && (
        <Box p={4} bg="green.50" borderRadius="lg" border="1px" borderColor="green.200">
          <Text fontSize="sm" color="green.600" fontWeight="semibold">
            مشاور مراجع:
          </Text>
          <Text fontSize="sm" color="green.700" mt={1}>
            {clientConsultant.name} - {clientConsultant.specialty || 'بدون تخصص'}
          </Text>
          <Text fontSize="xs" color="green.600" mt={1}>
            طول هر جلسه: {clientConsultant.sessionDuration || 60} دقیقه
          </Text>
        </Box>
      )}

      {/* هشدار عدم وجود مشاور */}
      {selectedClient && !clientConsultant && (
        <Alert status="warning" borderRadius="lg">
          <AlertIcon as={FaExclamationTriangle} />
          <Box>
            <AlertTitle>مشاور تعیین نشده!</AlertTitle>
            <AlertDescription>
              این مراجع مشاور مشخصی ندارد. لطفاً ابتدا مشاور وی را تعیین کنید و سپس ملاقات را ثبت نمایید.
            </AlertDescription>
          </Box>
        </Alert>
      )}

      {/* Hidden input for validation */}
      <input
        type="hidden"
        {...register('clientId', {
          required: 'انتخاب مراجع الزامی است'
        })}
      />
      <input
        type="hidden"
        {...register('consultantId', {
          required: 'مشاور مراجع الزامی است'
        })}
      />
    </VStack>
  );

  const renderStep2 = () => (
    <VStack spacing={6} align="stretch">
      <Box p={4} bg="green.50" borderRadius="lg" border="1px" borderColor="green.200">
        <Text fontSize="sm" color="green.600" fontWeight="semibold">
          مرحله 2: انتخاب تاریخ و زمان
        </Text>
        <Text fontSize="xs" color="green.500" mt={1}>
          تاریخ و زمان مناسب را انتخاب کنید
        </Text>
      </Box>

      <Grid templateColumns="repeat(2, 1fr)" gap={6}>
        <GridItem>
          <FormControl isInvalid={errors.date}>
            
            <JalaliDatePicker
              selectedDate={selectedDate}
              onDateChange={handleDateChange}
              placeholder="تاریخ را انتخاب کنید"
              required={true}
              isInvalid={!!errors.date}
              errorMessage={errors.date?.message}
              saveAsJalali={true}
            />
            <FormErrorMessage>
              {errors.date && errors.date.message}
            </FormErrorMessage>
          </FormControl>
        </GridItem>

        <GridItem>
          <FormControl>
            <FormLabel>طول جلسه (دقیقه)</FormLabel>
            <Input
              value={clientConsultant?.sessionDuration || 60}
              isReadOnly
              bg="gray.50"
            />
            <Text fontSize="xs" color="gray.500" mt={1}>
              طول جلسه بر اساس تنظیمات مشاور تعیین می‌شود
            </Text>
          </FormControl>
        </GridItem>
      </Grid>

      {selectedDate && clientConsultant && (
        <Box>
          <FormControl isInvalid={errors.time}>
            <FormLabel>زمان *</FormLabel>
            {availableSlots.length > 0 ? (
              <SimpleGrid columns={{ base: 2, md: 3 }} spacing={3}>
                {availableSlots.map((slot) => (
                 <Box
                 key={slot.time}
                 cursor={slot.status === "active" ? "pointer" : "not-allowed"}
                 onClick={() => slot.status === "active" && handleTimeSelect(slot.time)}
                 _hover={{ bg: slot.status === "active" ? "blue.50" : undefined }}
                 border="1px"
                 borderColor={
                   slot.status === "active"
                     ? (watch('time') === slot.time ? "blue.500" : "green.500")
                     : "gray.300"
                 }
                 borderRadius="md"
                 p={3}
                 bg={
                   slot.status === "active"
                     ? (watch('time') === slot.time ? "blue.50" : "green.50")
                     : "gray.100"
                 }
                 textAlign="center"
                 opacity={slot.status === "active" ? 1 : 0.6}
                 pointerEvents={slot.status === "active" ? "auto" : "none"}
               >
                 <Text 
                   fontWeight="bold" 
                   fontSize="sm"
                   color={
                     slot.status === "active"
                       ? (watch('time') === slot.time ? "blue.600" : "green.600")
                       : "gray.500"
                   }
                 >
                   {slot.time}
                 </Text>
               </Box>
                ))}
              </SimpleGrid>
            ) : (
              <Alert status="warning" borderRadius="md">
                <AlertIcon />
                <Text fontSize="sm">
                  {selectedDate && clientConsultant 
                    ? 'در این تاریخ ساعات کاری موجود نیست'
                    : 'ابتدا تاریخ را انتخاب کنید'
                  }
                </Text>
              </Alert>
            )}
            <FormErrorMessage>
              {errors.time && errors.time.message}
            </FormErrorMessage>
          </FormControl>
          {/* Hidden input for validation */}
          <input
            type="hidden"
            {...register('time', {
              required: 'انتخاب زمان الزامی است'
            })}
          />
        </Box>
      )}

      {/* Hidden input for duration */}
      <input
        type="hidden"
        {...register('duration')}
      />
    </VStack>
  );

  const renderStep3 = () => (
    <VStack spacing={6} align="stretch">
      <Box p={4} bg="purple.50" borderRadius="lg" border="1px" borderColor="purple.200">
        <Text fontSize="sm" color="purple.600" fontWeight="semibold">
          مرحله 3: تنظیمات تکمیلی
        </Text>
        <Text fontSize="xs" color="purple.500" mt={1}>
          تنظیمات اضافی ملاقات را مشخص کنید
        </Text>
      </Box>

      {isAdmin() && (
        <Grid templateColumns="repeat(2, 1fr)" gap={6}>
          <GridItem>
            <FormControl>
              <FormLabel>هزینه (تومان)</FormLabel>
              <NumberInput
                min={0}
                onChange={(value) => setValue('price', parseInt(value))}
              >
                <NumberInputField
                  {...register('price')}
                  placeholder="مبلغ به تومان"
                />
                <NumberInputStepper>
                  <NumberIncrementStepper />
                  <NumberDecrementStepper />
                </NumberInputStepper>
              </NumberInput>
            </FormControl>
          </GridItem>

          <GridItem>
            <FormControl>
              <FormLabel>وضعیت</FormLabel>
              <Select {...register('status')}>
                <option value="scheduled">تأیید شده</option>
                <option value="pending">در انتظار</option>
                <option value="cancelled">لغو شده</option>
              </Select>
            </FormControl>
          </GridItem>

          <GridItem>
            <FormControl>
              <FormLabel>وضعیت پرداخت</FormLabel>
              <Select {...register('paymentStatus')}>
                <option value="pending">در انتظار</option>
                <option value="paid">پرداخت شده</option>
                <option value="partial">نیمه پرداخت</option>
              </Select>
            </FormControl>
          </GridItem>

          <GridItem>
            <FormControl>
              <FormLabel>نوع ملاقات</FormLabel>
              <Select {...register('meetingType')}>
                <option value="in-person">حضوری</option>
                <option value="online">آنلاین</option>
                <option value="phone">تلفنی</option>
              </Select>
            </FormControl>
          </GridItem>
        </Grid>
      )}

      <Grid templateColumns="repeat(2, 1fr)" gap={6}>
        <GridItem>
          <FormControl>
            <FormLabel>نام همراه</FormLabel>
            <Input
              {...register('companionName')}
              placeholder="نام همراه (اختیاری)"
            />
          </FormControl>
        </GridItem>

        <GridItem>
          <FormControl>
            <FormLabel>نسبت همراه</FormLabel>
            <Input
              {...register('companionRelation')}
              placeholder="نسبت با مراجع (اختیاری)"
            />
          </FormControl>
        </GridItem>
      </Grid>

      <FormControl>
        <FormLabel>یادداشت</FormLabel>
        <Textarea
          {...register('notes')}
          placeholder="یادداشت اضافی (اختیاری)"
          rows={3}
        />
      </FormControl>
    </VStack>
  );

  const renderStep4 = () => {
    const formData = watch();
    const selectedClientData = clients.find(c => c._id === formData.clientId);
    const selectedConsultantData = consultants.find(c => c._id === formData.consultantId);

    return (
      <VStack spacing={6} align="stretch">
        <Box p={4} bg="orange.50" borderRadius="lg" border="1px" borderColor="orange.200">
          <Text fontSize="sm" color="orange.600" fontWeight="semibold">
            مرحله 4: مرور و ثبت
          </Text>
          <Text fontSize="xs" color="orange.500" mt={1}>
            اطلاعات را بررسی کرده و ملاقات را ثبت کنید
          </Text>
        </Box>

        <Box p={4} bg={bgCard} borderRadius="lg" border="1px" borderColor={borderColor}>
          <Text fontSize="lg" fontWeight="bold" color={textColor} mb={4}>
            خلاصه اطلاعات ملاقات
          </Text>
          
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
            <VStack align="start" spacing={2}>
              <Text fontSize="sm" color="gray.500">مراجع:</Text>
              <Text fontWeight="bold">
                {selectedClientData ? `${selectedClientData.firstName} ${selectedClientData.lastName}` : 'انتخاب نشده'}
              </Text>
            </VStack>

            <VStack align="start" spacing={2}>
              <Text fontSize="sm" color="gray.500">مشاور:</Text>
              <Text fontWeight="bold">
                {selectedConsultantData ? selectedConsultantData.name : 'انتخاب نشده'}
              </Text>
            </VStack>

            <VStack align="start" spacing={2}>
              <Text fontSize="sm" color="gray.500">تاریخ:</Text>
              <Text fontWeight="bold">{formData.date || 'انتخاب نشده'}</Text>
            </VStack>

            <VStack align="start" spacing={2}>
              <Text fontSize="sm" color="gray.500">زمان:</Text>
              <Text fontWeight="bold">{formData.time || 'انتخاب نشده'}</Text>
            </VStack>

            <VStack align="start" spacing={2}>
              <Text fontSize="sm" color="gray.500">مدت:</Text>
              <Text fontWeight="bold">{formData.duration || 60} دقیقه</Text>
            </VStack>

            {isAdmin() && (
              <VStack align="start" spacing={2}>
                <Text fontSize="sm" color="gray.500">هزینه:</Text>
                <Text fontWeight="bold">{formData.price || 0} تومان</Text>
              </VStack>
            )}
          </SimpleGrid>
        </Box>
      </VStack>
    );
  };

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1:
        return renderStep1();
      case 2:
        return renderStep2();
      case 3:
        return renderStep3();
      case 4:
        return renderStep4();
      default:
        return null;
    }
  };

  const totalSteps = 4; // همیشه 4 مرحله

  return (
    <Box>
      {/* Progress Bar */}
      <Box mb={6}>
        <HStack spacing={2} mb={2}>
          {Array.from({ length: totalSteps }, (_, i) => (
            <Box
              key={i}
              w="full"
              h={2}
              bg={i < currentStep ? primaryColor : 'gray.200'}
              borderRadius="full"
              transition="all 0.3s"
            />
          ))}
        </HStack>
        <Text fontSize="sm" color="gray.500" textAlign="center">
          مرحله {currentStep} از {totalSteps}
        </Text>
      </Box>

      {/* Step Title */}
      <Box mb={6} textAlign="center">
        <HStack justify="center" spacing={2} mb={2}>
          {getStepIcon(currentStep)}
          <Text fontSize="lg" fontWeight="bold" color={textColor}>
            {getStepTitle(currentStep)}
          </Text>
        </HStack>
      </Box>

      {/* Step Content */}
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        {renderCurrentStep()}

        {/* Navigation Buttons */}
        <HStack spacing={4} mt={8} justify="space-between">
          <Button
            leftIcon={<FaArrowRight />}
            variant="outline"
            onClick={prevStep}
            isDisabled={currentStep === 1}
          >
            مرحله قبل
          </Button>

          {currentStep < totalSteps ? (
            <Button
              rightIcon={<FaArrowLeft />}
              colorScheme="teal"
              onClick={nextStep}
            >
              مرحله بعد
            </Button>
          ) : (
            <Button
              type="submit"
              colorScheme="teal"
              isLoading={isLoading}
              loadingText="در حال ثبت..."
            >
              ثبت ملاقات
            </Button>
          )}
        </HStack>
      </form>
    </Box>
  );
});

export default MultiStepAppointmentForm;
